import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { User, Lock, LogOut, Shield } from "lucide-react";

const ProfilePage = () => {
  const { user, signOut } = useAuth();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Profile fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");

  // Password fields
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    if (!user) return;
    setEmail(user.email || "");
    // Fetch profile data
    supabase
      .from("profiles")
      .select("name, avatar_url")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setName(data.name || "");
          setAvatarUrl(data.avatar_url || "");
        }
      });
  }, [user]);

  const handleSaveProfile = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          name: name.trim() || null,
          avatar_url: avatarUrl.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);

      if (error) throw error;
      toast.success("Profile updated successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (passwordLoading) return;

    // 1. Required fields validation
    if (!currentPassword.trim()) {
      toast.error("Please enter your current password");
      return;
    }
    if (!newPassword) {
      toast.error("Please enter your new password");
      return;
    }
    if (!confirmPassword) {
      toast.error("Please confirm your new password");
      return;
    }

    // 2. Minimum password policy (at least 8 characters)
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters long");
      return;
    }

    // 3. Prevent using same password
    if (newPassword === currentPassword) {
      toast.error("New password must be different from your current password");
      return;
    }

    // 4. Matching confirmation check
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation password do not match");
      return;
    }

    setPasswordLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
        current_password: currentPassword,
      });

      if (error) throw error;

      toast.success("Password updated successfully! Your new password is now active.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      toast.error(err.message || "Failed to update password. Please check your current password.");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogoutAll = async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: "global" });
      if (error) throw error;
      localStorage.clear();
      sessionStorage.clear();
      window.location.assign("/login");
    } catch (err: any) {
      toast.error(err.message || "Failed to logout from all devices");
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground">Profile Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your account information and security</p>
      </div>

      {/* Profile Information */}
      <div className="p-6 rounded-lg bg-card border border-border space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <User className="h-5 w-5 text-accent" />
          <h2 className="text-lg font-semibold text-foreground">Personal Information</h2>
        </div>

        <div>
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Full Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" className="mt-1.5" />
        </div>

        <div>
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</Label>
          <Input value={email} disabled className="mt-1.5 opacity-60" />
          <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
        </div>

        <div>
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Profile Picture URL</Label>
          <Input value={avatarUrl} onChange={(e) => setAvatarUrl(e.target.value)} placeholder="https://..." className="mt-1.5" />
        </div>

        <Button onClick={handleSaveProfile} disabled={loading} className="bg-accent text-accent-foreground hover:bg-accent/90">
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Change Password */}
      <div className="p-6 rounded-lg bg-card border border-border space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Lock className="h-5 w-5 text-accent" />
          <h2 className="text-lg font-semibold text-foreground">Change Password</h2>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-4" autoComplete="off">
          <div>
            <Label htmlFor="current-password" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Current Password *
            </Label>
            <Input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter your current password"
              autoComplete="current-password"
              className="mt-1.5"
              required
            />
          </div>

          <div>
            <Label htmlFor="new-password" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              New Password * (Min. 8 characters)
            </Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min. 8 characters)"
              autoComplete="new-password"
              className="mt-1.5"
              required
            />
          </div>

          <div>
            <Label htmlFor="confirm-password" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Confirm New Password *
            </Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your new password"
              autoComplete="new-password"
              className="mt-1.5"
              required
            />
          </div>

          <Button type="submit" disabled={passwordLoading} variant="outline" className="gap-2">
            {passwordLoading ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary inline-block mr-1" />
                Updating Password...
              </>
            ) : (
              <>
                <Lock className="h-4 w-4" />
                Update Password
              </>
            )}
          </Button>
        </form>
      </div>

      {/* Security */}
      <div className="p-6 rounded-lg bg-card border border-border space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="h-5 w-5 text-accent" />
          <h2 className="text-lg font-semibold text-foreground">Security</h2>
        </div>

        <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border/50">
          <div>
            <p className="text-sm font-medium text-foreground">Logout from all devices</p>
            <p className="text-xs text-muted-foreground">This will sign you out everywhere including this device</p>
          </div>
          <Button onClick={handleLogoutAll} variant="destructive" size="sm">
            <LogOut className="h-4 w-4 mr-1" /> Logout All
          </Button>
        </div>

        <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border/50">
          <div>
            <p className="text-sm font-medium text-foreground">Current Session</p>
            <p className="text-xs text-muted-foreground">Logged in as {email}</p>
          </div>
          <Button onClick={() => signOut()} variant="outline" size="sm">
            <LogOut className="h-4 w-4 mr-1" /> Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
